import { useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { api, ApiError, type Referentials } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { formatDate } from '../labels';
import { Icon } from '../components/icons';
import { SPACES, visibleSpaces } from '../spaces';
import '../styles/compte.css';

let referentialsCache: Promise<Referentials> | null = null;

export function useReferentials(): Referentials | null {
  const [data, setData] = useState<Referentials | null>(null);
  useEffect(() => {
    referentialsCache ??= api<Referentials>('/referentiels');
    referentialsCache.then(setData).catch(() => {
      referentialsCache = null;
    });
  }, []);
  return data;
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <p>Chargement…</p>;
  if (status !== 'authenticated')
    return <Navigate to="/connexion" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

const SPACE_ICONS: Record<string, string> = {
  demandes: 'file',
  guichet: 'desk',
  juridictions: 'court',
  archives: 'archive',
  casier: 'registry',
  delivrance: 'stamp',
  organismes: 'building',
  finance: 'wallet',
  pilotage: 'chart',
  audit: 'shield',
  habilitations: 'key',
  technique: 'gear',
};

const KIND_LABELS: Record<string, string> = {
  agent: 'Agent',
  usager: 'Usager',
  organisme: 'Organisme habilité',
  technique: 'Exploitation',
};

const MINOR_WORDS = new Set(['de', 'du', 'des', 'la', 'le', 'les', 'd', 'l']);

function initials(name: string): string {
  return name
    .split(/[\s'’-]+/)
    .filter((w) => w && !MINOR_WORDS.has(w.toLowerCase()))
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

export function WorkspaceLayout() {
  const { me } = useAuth();
  if (!me) return null;
  const spaces = visibleSpaces(me.permissions);
  const name = me.displayName.replace(/ \(démo\)$/, '');
  return (
    <div className="app">
      <aside className="side" aria-label="Mes espaces">
        <div className="who">
          <span className="av" aria-hidden="true">
            {initials(name)}
          </span>
          <span>
            <b>{name}</b>
            <span className="muted">{KIND_LABELS[me.kind]}</span>
          </span>
        </div>
        <NavLink to="/espace" end>
          <Icon name="user" />
          Mon compte
        </NavLink>
        {spaces.length > 0 && <span className="lab">Mes espaces</span>}
        {spaces.map((s) => (
          <NavLink key={s.key} to={s.path}>
            <Icon name={SPACE_ICONS[s.key] ?? 'file'} />
            {s.label}
            {s.plannedLot && <span className="badge">{s.plannedLot}</span>}
          </NavLink>
        ))}
      </aside>
      <div className="main">
        <Outlet />
      </div>
    </div>
  );
}

export function AccountPage() {
  const { me } = useAuth();
  const refs = useReferentials();
  if (!me) return null;
  const profileLabel = (code: string) => refs?.profiles.find((p) => p.code === code)?.label ?? code;
  const orgName = (id: string) => refs?.organizations.find((o) => o.id === id)?.name ?? '…';
  const jurName = (id: string) => refs?.jurisdictions.find((j) => j.id === id)?.name ?? '…';
  const spaces = visibleSpaces(me.permissions);

  return (
    <div className="stack">
      <div className="head rise">
        <h1 className="acct-title">
          Bonjour, <span className="muted">{me.displayName}</span>
        </h1>
      </div>
      {me.mfaWithheld > 0 && (
        <div className="alert alert-warn" role="alert">
          {me.mfaWithheld} habilitation(s) d’agent ne sont pas actives pour cette session : elles exigent une
          authentification multifacteur.
        </div>
      )}
      {spaces.length === 0 && me.mfaWithheld === 0 && (
        <div className="alert alert-info">
          Aucune habilitation active. Adressez-vous à votre gestionnaire.
        </div>
      )}

      <div className="cols acct-cols">
        {spaces.map((s) => (
          <NavLink key={s.key} to={s.path} className="card acct-space">
            <div className="ch">
              <span className="tile-icon">
                <Icon name={SPACE_ICONS[s.key] ?? 'file'} />
              </span>
              {s.plannedLot ? (
                <span className="chip">{s.plannedLot}</span>
              ) : (
                <span className="chip ok">Disponible</span>
              )}
            </div>
            <h2>{s.label}</h2>
            <p>{s.description}</p>
            <span className="lnk" aria-hidden="true">
              Ouvrir ›
            </span>
          </NavLink>
        ))}
        <section className="card tint acct-sec" aria-labelledby="acct-securite">
          <h2 id="acct-securite">Sécurité</h2>
          <p>
            Authentification multifacteur : <strong>{me.mfa ? 'oui' : 'non'}</strong>.
          </p>
          <p className="muted">Vos droits sont recalculés à chaque action.</p>
        </section>
      </div>

      <section className="card acct-hab" aria-labelledby="acct-habilitations">
        <h2 id="acct-habilitations">Mes habilitations actives</h2>
        {me.habilitations.length === 0 ? (
          <p className="muted">Aucune.</p>
        ) : (
          <div className="table-wrap tbl-inset">
            <table>
              <thead>
                <tr>
                  <th scope="col">Profil</th>
                  <th scope="col">Organisme</th>
                  <th scope="col">Compétence</th>
                  <th scope="col">Échéance</th>
                </tr>
              </thead>
              <tbody>
                {me.habilitations.map((h) => (
                  <tr key={h.id}>
                    <td data-l="Profil">
                      {profileLabel(h.profileCode)}{' '}
                      {h.isEmergency && <span className="badge badge-warn">Urgence</span>}
                    </td>
                    <td data-l="Organisme">{orgName(h.organizationId)}</td>
                    <td data-l="Compétence">
                      {h.scope === 'national' ? 'Nationale' : h.jurisdictionIds.map(jurName).join(', ')}
                    </td>
                    <td data-l="Échéance">{formatDate(h.validUntil)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export function PlannedSpacePage({ spaceKey }: { spaceKey: string }) {
  const space = SPACES.find((s) => s.key === spaceKey)!;
  return (
    <div className="stack">
      <div className="head rise">
        <div>
          <h1>{space.label}</h1>
          <p className="lead">{space.description}</p>
        </div>
      </div>
      <section className="card planned">
        <span className="tile-icon lg" aria-hidden="true">
          <Icon name={SPACE_ICONS[space.key] ?? 'file'} size={22} />
        </span>
        <p className="alert alert-info">
          Cet espace sera livré avec le {space.plannedLot} du programme de réalisation. Votre habilitation y
          donne déjà accès : il apparaîtra ici automatiquement.
        </p>
      </section>
    </div>
  );
}

interface Integrity {
  ok: boolean;
  checked: number;
  head: { seq: string; hash: string } | null;
  failures: { seq: string; reason: string; detail: string }[];
}

export function AuditPage() {
  const [result, setResult] = useState<Integrity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      setResult(await api<Integrity>('/audit/integrite'));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Vérification impossible.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="stack">
      <div className="head rise">
        <div>
          <h1>Audit</h1>
          <p className="lead">Contrôle de l’intégrité du journal des événements.</p>
        </div>
      </div>
      <section className="card audit-main" aria-labelledby="audit-integrite">
        <span className="tile-icon lg" aria-hidden="true">
          <Icon name="shield" size={22} />
        </span>
        <h2 id="audit-integrite">Intégrité du journal</h2>
        <p>
          Chaque événement du journal porte l’empreinte du précédent. La vérification recalcule toute la
          chaîne et signale toute modification, suppression ou réécriture.
        </p>
        <button className="btn audit-run" type="button" onClick={() => void run()} disabled={busy}>
          {busy ? 'Vérification…' : 'Vérifier maintenant'}
        </button>
        {error && <p className="alert alert-danger">{error}</p>}
        {result && (
          <div className={`alert ${result.ok ? 'alert-ok' : 'alert-danger'}`} role="status">
            <strong>{result.ok ? 'Journal intègre.' : 'Altération détectée.'}</strong> {result.checked}{' '}
            entrée(s) vérifiée(s).
            {result.head && (
              <div className="small" style={{ marginTop: 8 }}>
                Tête à ancrer hors du système :{' '}
                <span className="mono">
                  {result.head.seq}:{result.head.hash}
                </span>
              </div>
            )}
            {result.failures.map((f) => (
              <div key={`${f.seq}-${f.reason}`} className="small">
                Entrée {f.seq} : {f.detail}
              </div>
            ))}
          </div>
        )}
      </section>
      <div className="audit-steps">
        <div className="card tint">
          <span className="num" aria-hidden="true">
            1
          </span>
          <h3>Empreinte</h3>
          <p className="muted">Chaque événement est scellé.</p>
        </div>
        <div className="card tint">
          <span className="num" aria-hidden="true">
            2
          </span>
          <h3>Chaînage</h3>
          <p className="muted">Il reprend l’empreinte du précédent.</p>
        </div>
        <div className="card tint">
          <span className="num" aria-hidden="true">
            3
          </span>
          <h3>Contrôle</h3>
          <p className="muted">Toute rupture de chaîne est signalée.</p>
        </div>
      </div>
    </div>
  );
}
